import { SelectValue } from "antd/es/select";
import moment from "moment/moment";
import { Col, ConfigProvider, Form, FormInstance, Radio, Row, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { DEFAULT_MAX_PAGINATION, validateMessages } from "../../../../../constants/common";
import styles from "./index.module.scss";
import { PromotionDiscountType, RecurlyCouponModel } from "../../../../../types/model/promotion";
import * as basePriceAPI from "../../../../../services/basePrice";
import * as promotionAPI from "../../../../../services/promotion";
import { BasePriceModel, PriceStatus } from "../../../../../types/model/price";
import { getDisplayDiscount, getDisplayPromotionDiscountType, getDisplayPromotionStatus } from "../../util";
import { DescriptionAndDisclosure } from "../DescriptionAndDisclosure";
import * as eComProductAPI from "../../../../../services/eComProduct";
import { EComProductModel } from "../../../../../types/model/eComProduct";
import PromotionDetailsFormForRecurlySource from "./PromotionDetailsFormForRecurlySource";
import PromotionDetailsFormForEComSource from "./PromotionDetailsFormForEComSource";
import { usePromotionPageStore } from "../../usePromotionPageStore";

export enum PromotionSource {
  RECURLY = "Recurly",
  BUY_ONE_GIFT_ONE = "Buy one gift one",
}

export interface Option {
  label: string;
  value: number;
}

interface PromotionFormProps {
  form: FormInstance;
  isEditing?: boolean;
}

export function PromotionForm({ form, isEditing = false }: PromotionFormProps) {
  const descriptionDisclosures = usePromotionPageStore((state) => state.descriptionDisclosures);
  const [promotionSource, setPromotionSource] = useState<PromotionSource>(PromotionSource.RECURLY);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recurlyCoupons, setRecurlyCoupons] = useState<RecurlyCouponModel[]>([]);
  const [basePriceList, setBasePriceList] = useState<BasePriceModel[]>([]);
  const [countryList, setCountryList] = useState<Option[]>([]);
  const [planOptionList, setPlanOptionList] = useState<Option[]>([]);
  const [giftPlanList, setGiftPlanList] = useState<EComProductModel[]>([]);
  const [appliedBillingPlanIds, setAppliedBillingPlanIds] = useState<string[]>([]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      promotionAPI.fetchAllValidRecurlyCoupons(),
      basePriceAPI.fetchAllCountries(),
      basePriceAPI.fetchBasePriceList({
        status: PriceStatus.ACTIVE,
        page: 1,
        limit: DEFAULT_MAX_PAGINATION.size,
      }),
      eComProductAPI.fetchProductList({
        limit: DEFAULT_MAX_PAGINATION.size,
        type: "GiftCode",
      }),
    ])
      .then(([allValidRecurlyCoupons, countries, basePriceList, giftPlanList]) => {
        setRecurlyCoupons(allValidRecurlyCoupons);
        setCountryList(countries.map((item) => ({ label: item.displayName, value: item.id })));
        setBasePriceList(basePriceList.list);
        setGiftPlanList(giftPlanList.list);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const currentPromotionSource = form.getFieldValue("source");
    setPromotionSource(currentPromotionSource);

    if (currentPromotionSource === PromotionSource.BUY_ONE_GIFT_ONE && descriptionDisclosures[0]) {
      const { description, disclosure } = descriptionDisclosures[0];
      if (description) {
        description?.forEach((item) => {
          form.setFieldsValue({
            [`${item.locale}DescriptionContent`]: item.content,
          });
        });
      }
      if (disclosure) {
        disclosure?.forEach((item) => {
          form.setFieldsValue({
            [`${item.locale}DisclosureContent`]: item.content,
          });
        });
      }

      handleVariantsSearch(form.getFieldValue("basePlanPid")).then();
    }
  }, [descriptionDisclosures, form]);

  const handlePromotionSourceChange = (source: PromotionSource) => {
    setPromotionSource(source);
    form.resetFields();
    form.setFieldsValue({
      source,
    });

    if (source === PromotionSource.BUY_ONE_GIFT_ONE) {
      form.setFieldsValue({
        type: getDisplayPromotionDiscountType(PromotionDiscountType.BUY_ONE_GIFT_ONE),
      });
    }
  };

  const handlePromotionCodeChange = (selectedRecurlyCouponId: SelectValue) => {
    const coupon = recurlyCoupons.find((coupon) => coupon.id === selectedRecurlyCouponId);
    form.setFieldsValue({
      ...form?.getFieldsValue(),
      code: coupon!.code,
      recurlyCouponId: coupon!.id,
      name: coupon!.name,
      timeRange: [moment(coupon!.startedAt).local(), coupon!.endedAt ? moment(coupon!.endedAt).local() : null],
      type: getDisplayPromotionDiscountType(coupon!.discountType),
      discount: getDisplayDiscount(coupon!.discountType, coupon?.percent, coupon?.currencies),
      status: getDisplayPromotionStatus(coupon!.status),
    });
  };

  const handleVariantsSearch = async (search: string): Promise<Option[]> => {
    const res = await eComProductAPI.fetchProductVariantsByFuzzySearch(search);
    const options = res
      ? res.map(({ id, title, pid, billingPlan }) => ({
          key: id,
          label: billingPlan ? `${pid} ${title} / ${billingPlan.name}` : `${pid} ${title}`,
          value: pid,
        }))
      : [];
    setPlanOptionList(options);
    return options;
  };

  return (
    <Spin spinning={isLoading} size="large">
      <ConfigProvider form={{ validateMessages }}>
        <Form
          form={form}
          validateTrigger={"onBlur"}
          layout="vertical"
          className={styles.promotionForm}
          onValuesChange={(changedValues) => {
            if (changedValues.appliedBillingPlanIds) {
              setAppliedBillingPlanIds(changedValues.appliedBillingPlanIds);
            }
          }}
        >
          <header className={styles.subtitle}>Promotion details</header>
          <Row gutter={32}>
            <Col span={24}>
              <Form.Item
                label="Promotion source"
                name="source"
                rules={[{ required: true }]}
                initialValue={PromotionSource.RECURLY}
              >
                <Radio.Group onChange={(e) => handlePromotionSourceChange(e.target.value)} disabled={isEditing}>
                  {Object.values(PromotionSource).map((type) => (
                    <Radio value={type} key={type} data-testid={`promotion-source-${type}`}>
                      {type}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          {promotionSource === PromotionSource.RECURLY && (
            <PromotionDetailsFormForRecurlySource
              form={form}
              recurlyCoupons={recurlyCoupons}
              basePriceList={basePriceList}
              countryList={countryList}
              handlePromotionCodeChange={handlePromotionCodeChange}
              isEditing={isEditing}
            />
          )}

          {promotionSource === PromotionSource.BUY_ONE_GIFT_ONE && (
            <PromotionDetailsFormForEComSource
              planOptionList={planOptionList}
              setPlanOptionList={setPlanOptionList}
              handleVariantsSearch={handleVariantsSearch}
              giftPlanList={giftPlanList}
            />
          )}
        </Form>

        {promotionSource === PromotionSource.RECURLY && (
          <DescriptionAndDisclosure editable={true} allowedBillingPlanIds={appliedBillingPlanIds} />
        )}
      </ConfigProvider>
    </Spin>
  );
}
